import React, { useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../lib/cn";
import { useOverlay, usePresence, useNavbarOffset, useSidebarOffset } from "./overlayBehavior";

/**
 * Drawer — ui-v2.5. Panel lateral derecho. Especificación: catálogo · sección Drawer.
 * Variantes: `brand` (cuerpo con fondo hundido, para detalle de entidad) y `plain` (cuerpo blanco,
 * para formularios). La cabecera y el pie son slots (`header`, `footer`) que el consumidor estiliza
 * — la de marca (#003c66 con avatar y barra) y la neutra se construyen fuera, porque son específicas.
 *
 * Comportamiento (vía useOverlay): Escape (respetando apilado), foco atrapado, retorno de foco,
 * scroll del body bloqueado sin salto. Animación translateX .32s cubic-bezier(.4,0,.2,1). Velo cierra al clic.
 *
 * <Drawer open onClose variant="brand|plain" width={580} header={<…/>} footer={<…/>}>{cuerpo}</Drawer>
 */

export function Drawer({ open, onClose, variant = "plain", width = 580, header, footer, children, className }) {
  const panelRef = useRef(null);
  const { mounted, shown } = usePresence(open, 320);
  const navOffset = useNavbarOffset();
  const sidebarOffset = useSidebarOffset();
  useOverlay({ active: open && mounted, onClose, panelRef });

  if (!mounted) return null;

/**
 * PORTAL · el overlay se monta en `document.body`, no donde lo escriba el consumidor.
 *
 * Sin portal, hereda el arbol en el que lo montas — y eso tiene una consecuencia que no se ve:
 * **dentro de un `<form>`, un `<form>` anidado es HTML invalido**, asi que el boton `type="submit"`
 * del overlay acaba enviando el formulario EXTERIOR por el camino nativo. La pagina se recarga, el
 * manejador del overlay nunca corre, y no hay ningun error.
 *
 * El diagnostico se ve en la URL: **una interrogacion vacia al final** (`/editarPersona/2?`) es la
 * firma de una navegacion nativa por GET.
 *
 * Lo que el portal NO cambia: `useNavbarOffset` y `useSidebarOffset` miden nodos GLOBALES
 * (`[data-testid="app-navbar"]`, `#mySidebar`), no ancestros del overlay; el bloqueo de scroll, la
 * pila de Escape y el retorno de foco trabajan sobre `document`; y el foco atrapado, sobre
 * `panelRef`. Todo eso es indiferente a donde cuelgue el nodo.
 *
 * Lo que SI podria haber cambiado, y se midio antes y despues: un `position: fixed` deja de
 * referirse al viewport si algun ANCESTRO tiene transform/filter/will-change. Ninguno de los
 * overlays medidos lo tenia, y las posiciones salen identicas.
 */
  return createPortal(
    // El contenedor arranca bajo el NavBar (top:navOffset) y a la derecha del sidebar (left:sidebarOffset):
    // el panel va anclado a la derecha (no se recortaba), pero así el VELO también empieza donde empieza el
    // contenido y no se pinta bajo el sidebar. Opción (a), como el Drawer de inventario.
    <div className="fixed bottom-0 right-0 z-50" style={{ top: navOffset, left: sidebarOffset }}>
      <div
        className={cn("absolute inset-0 bg-black/25 transition-opacity duration-300", shown ? "opacity-100" : "opacity-0")}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        style={{ width, maxWidth: "92vw", transitionTimingFunction: "cubic-bezier(.4,0,.2,1)", transitionDuration: "320ms" }}
        className={cn(
          "absolute right-0 top-0 flex h-full flex-col overflow-hidden bg-white shadow-v25-drawer transition-transform",
          shown ? "translate-x-0" : "translate-x-full",
          className
        )}
      >
        {header}
        <div className={cn("flex-1 overflow-y-auto", variant === "brand" ? "space-y-2 bg-v25-surface-sunken p-4" : "bg-white")}>
          {children}
        </div>
        {footer}
      </div>
    </div>
  , document.body);
}
